/**
 * One supplier's figures at ONE customer site, split by supplier code —
 * CR-7288's third tier, under the per-site rows.
 *
 * A customer issues a supplier one code per ACCOUNT and a relationship can
 * hold several; the site row above covers them all. Each code here is asked of
 * the SAME list endpoint with `supplier_code` added, so every row is computed
 * by the definitions behind the row above it and the rows sum to it — with the
 * `(no code)` bucket carrying the lines that have no code at all, without
 * which they would not.
 */
import { suppliersAPI, SupplierProfile } from '../../lib/api';
import { useFetchData } from '../../hooks/useFetchData';

/** `null` is the `(no code)` bucket, not a missing value. */
export type BreakdownCode = string | null;

export interface CodeBreakdownRow {
  code: BreakdownCode;
  /** Null when the endpoint returned no row for that code (nothing in scope). */
  profile: SupplierProfile | null;
}

/**
 * Fetch one supplier's per-code rows at one site while `enabled`.
 *
 * @param supplierId - the supplier company id
 * @param siteId - the customer site the codes belong to
 * @param codes - the codes that site holds, issued ∪ seen in its lines
 * @param includeNoCode - also ask for the `(no code)` bucket
 * @param buildParams - the page's params for one code × site × supplier
 * @param enabled - fetch only once the site row is expanded
 * @returns `{ rows, loading, error, refetch }`
 */
export function useCodeBreakdown(
  supplierId: number,
  siteId: number,
  codes: string[],
  includeNoCode: boolean,
  buildParams: (siteId: number, supplierId: number, code: BreakdownCode) => Record<string, string | number | boolean>,
  enabled: boolean,
) {
  const wanted: BreakdownCode[] = includeNoCode ? [...codes, null] : [...codes];
  const codesKey = wanted.map((c) => c ?? '\u0000none').join(',');
  const paramsKey = JSON.stringify(buildParams(siteId, supplierId, ''));
  return useFetchData<CodeBreakdownRow[]>(
    async () => {
      if (!enabled) return [];
      const responses = await Promise.all(
        wanted.map((code) => suppliersAPI.getProfiles(buildParams(siteId, supplierId, code))),
      );
      return wanted.map((code, i) => ({
        code,
        profile: responses[i].data.results.find((r) => r.company === supplierId) ?? null,
      }));
    },
    { initialLoading: enabled, deps: [enabled, supplierId, siteId, codesKey, paramsKey] },
  );
}
